import React from 'react';
import { Grid, Card, CardContent, Typography, Box } from '@mui/material';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import PersonIcon from '@mui/icons-material/Person';
import ClaimTypeStyles from "./ClaimTypeStyles";

interface ClaimTypeProps {
  selectedClaimType: 'first-party' | 'third-party' | null;
  onClaimTypeSelect: (type: 'first-party' | 'third-party') => void;
}

const ClaimType: React.FC<ClaimTypeProps> = ({ selectedClaimType, onClaimTypeSelect }) => {
  return (
    <Box sx={ClaimTypeStyles.container}>
      <Typography 
        variant="h6" 
        gutterBottom 
        sx={ClaimTypeStyles.title}
        id="claimtype_title"
        data-test-id="test-claimtype_title"
        aria-label="Select Claim Type Section"
      >
        Select Claim Type
      </Typography>
      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <Card 
            sx={ClaimTypeStyles.card(selectedClaimType === "first-party")}
            onClick={() => onClaimTypeSelect("first-party")}
            id="claimtype_firstparty-card"
            data-test-id="test-claimtype_firstparty-card"
            aria-label="Select First Party Claim"
          >
            <CardContent sx={ClaimTypeStyles.cardContent}>
              <Box sx={ClaimTypeStyles.iconBox(selectedClaimType === "first-party")}> 
                <DirectionsCarIcon sx={{ fontSize: 40, mr: 2 }} />
                <Typography 
                  variant="h5" 
                  component="div" 
                  sx={ClaimTypeStyles.claimTypeText}
                  id="claimtype_firstparty-title"
                  data-test-id="test-claimtype_firstparty-title"
                >
                  First Party Claim
                </Typography>
              </Box>
              <Typography 
                variant="body1" 
                color="text.secondary" 
                sx={ClaimTypeStyles.description}
                id="claimtype_firstparty-description"
                data-test-id="test-claimtype_firstparty-description"
              >
                File a claim on your own insurance policy for an incident involving your insured vehicle.
              </Typography>
              {selectedClaimType === "first-party" && (
                <Box sx={ClaimTypeStyles.selectedBox}>
                  <Typography 
                    variant="body2" 
                    color="primary" 
                    sx={ClaimTypeStyles.selectedText}
                    id="claimtype_firstparty-selected"
                    data-test-id="test-claimtype_firstparty-selected"
                  >
                    Selected
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card 
            sx={ClaimTypeStyles.card(selectedClaimType === "third-party")}
            onClick={() => onClaimTypeSelect("third-party")}
            id="claimtype_thirdparty-card"
            data-test-id="test-claimtype_thirdparty-card"
            aria-label="Select Third Party Claim"
          >
            <CardContent sx={ClaimTypeStyles.cardContent}>
              <Box sx={ClaimTypeStyles.iconBox(selectedClaimType === "third-party")}> 
                <PersonIcon sx={{ fontSize: 40, mr: 2 }} />
                <Typography 
                  variant="h5" 
                  component="div" 
                  sx={ClaimTypeStyles.claimTypeText}
                  id="claimtype_thirdparty-title"
                  data-test-id="test-claimtype_thirdparty-title"
                >
                  Third Party Claim
                </Typography>
              </Box>
              <Typography 
                variant="body1" 
                color="text.secondary" 
                sx={ClaimTypeStyles.description}
                id="claimtype_thirdparty-description"
                data-test-id="test-claimtype_thirdparty-description"
              >
                File a claim against someone else's insurance policy for damages they caused.
              </Typography>
              {selectedClaimType === "third-party" && (
                <Box sx={ClaimTypeStyles.selectedBox}>
                  <Typography 
                    variant="body2" 
                    color="primary" 
                    sx={ClaimTypeStyles.selectedText}
                    id="claimtype_thirdparty-selected"
                    data-test-id="test-claimtype_thirdparty-selected"
                  >
                    Selected
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ClaimType; 